import { useEffect, useState, useMemo } from 'react';
import { ClipboardList, Search, RefreshCw, Clock, CheckCircle2, AlertTriangle, FilterX } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../ui/Spinner';
import { PageHeader } from '../ui/PageHeader';
import { fmtDateLong } from '../../lib/datetime';

/**
 * Pestaña admin: tareas TODO pendientes a nivel global.
 *
 * Para cada todo_task del campus, calculamos:
 *   - Cantidad de destinatarios (alumnos a los que se asignó)
 *   - Cuántos ya completaron
 *   - Estado derivado: completada (todos completaron), vencida (due_date pasó),
 *     pendiente (algunos sin completar y aún no vence)
 *
 * El admin puede filtrar por estado y buscar por título / curso / docente.
 */
interface TaskRow {
  id:           string;
  title:        string;
  description:  string | null;
  due_date:     string | null;
  created_at:   string;
  curso_name:   string;
  docente_name: string;
  total:        number;     // destinatarios
  completados:  number;
  derived:      'completada' | 'vencida' | 'pendiente';
}

type Filter = 'all' | 'pendiente' | 'vencida' | 'completada';

const FILTER_INFO: Record<Filter, { label: string; chip: string }> = {
  all:         { label: 'Todas',      chip: '' },
  pendiente:   { label: 'Pendientes', chip: 'chip-amber' },
  vencida:     { label: 'Vencidas',   chip: 'chip-rose' },
  completada:  { label: 'Completas',  chip: 'chip-emerald' },
};

export function TareasPendientesPage() {
  const [rows, setRows]   = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState<Filter>('pendiente');

  const load = async () => {
    setLoading(true);
    try {
      // Tomar todas las todo_tasks con info de curso y docente creador
      const { data: tasks } = await supabase
        .from('todo_tasks')
        .select(`
          id, title, description, due_date, created_at,
          courses ( name ),
          profiles:created_by ( full_name )
        `)
        .order('created_at', { ascending: false });

      if (!tasks || tasks.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      const taskIds = tasks.map((t: any) => t.id);
      // Recipients: contamos total y completados por task
      const { data: recipients } = await supabase
        .from('todo_task_recipients')
        .select('task_id, completed')
        .in('task_id', taskIds);

      const stats = new Map<string, { total: number; completados: number }>();
      (recipients ?? []).forEach((r: any) => {
        const cur = stats.get(r.task_id) ?? { total: 0, completados: 0 };
        cur.total++;
        if (r.completed) cur.completados++;
        stats.set(r.task_id, cur);
      });

      const now = Date.now();
      const out: TaskRow[] = tasks.map((t: any) => {
        const s = stats.get(t.id) ?? { total: 0, completados: 0 };
        const dueMs = t.due_date ? new Date(t.due_date).getTime() : null;
        let derived: TaskRow['derived'];
        if (s.total > 0 && s.completados >= s.total) derived = 'completada';
        else if (dueMs !== null && dueMs < now)      derived = 'vencida';
        else                                          derived = 'pendiente';
        return {
          id:           t.id,
          title:        t.title,
          description:  t.description ?? null,
          due_date:     t.due_date ?? null,
          created_at:   t.created_at,
          curso_name:   Array.isArray(t.courses)  ? (t.courses[0]?.name  ?? '—') : (t.courses?.name  ?? '—'),
          docente_name: Array.isArray(t.profiles) ? (t.profiles[0]?.full_name ?? '—') : (t.profiles?.full_name ?? '—'),
          total:        s.total,
          completados:  s.completados,
          derived,
        };
      });

      setRows(out);
    } catch (err) {
      console.error('[TareasPendientesPage] error:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    all:        rows.length,
    pendiente:  rows.filter(r => r.derived === 'pendiente').length,
    vencida:    rows.filter(r => r.derived === 'vencida').length,
    completada: rows.filter(r => r.derived === 'completada').length,
  }), [rows]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return rows.filter(r => {
      if (filter !== 'all' && r.derived !== filter) return false;
      if (!term) return true;
      return (
        r.title.toLowerCase().includes(term) ||
        r.curso_name.toLowerCase().includes(term) ||
        r.docente_name.toLowerCase().includes(term)
      );
    });
  }, [rows, search, filter]);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <PageHeader
        eyebrow="Análisis"
        eyebrowIcon={ClipboardList}
        title="Tareas pendientes del campus"
        description="Tareas TO-DO asignadas por docentes — vencidas, pendientes y completadas en un solo lugar."
        actions={
          <button onClick={load} disabled={loading} className="btn-glass" title="Recargar">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Recargar</span>
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="Total"       value={counts.all}        accent="text-white" />
        <StatPill label="Pendientes"  value={counts.pendiente}  accent="text-amber-400" />
        <StatPill label="Vencidas"    value={counts.vencida}    accent="text-rose-400" />
        <StatPill label="Completas"   value={counts.completada} accent="text-cubus-lime" />
      </div>

      <section className="glass-surface rounded-2xl p-4 border border-white/[0.06]">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="search"
              placeholder="Buscar por título, curso o docente…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-cubus-deep border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cubus-lime/40"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(['all', 'pendiente', 'vencida', 'completada'] as Filter[]).map(f => {
              const isActive = filter === f;
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-cubus-lime/20 text-cubus-lime border border-cubus-lime/40'
                      : 'bg-white/[0.04] text-white/60 border border-white/10 hover:bg-white/[0.08]'
                  }`}
                >
                  {FILTER_INFO[f].label} <span className="opacity-60">({counts[f]})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="glass-surface rounded-2xl border border-white/[0.06] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50">
            <FilterX className="w-10 h-10 mx-auto mb-3 text-white/30" />
            <p className="text-sm">
              {rows.length === 0 ? 'Aún no hay tareas creadas en el campus.' : 'No hay tareas que coincidan con el filtro.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-white/40">
                  <th className="text-left py-3 px-4">Tarea</th>
                  <th className="text-left py-3 px-4">Curso</th>
                  <th className="text-left py-3 px-4">Docente</th>
                  <th className="text-center py-3 px-4">Progreso</th>
                  <th className="text-left py-3 px-4">Vence</th>
                  <th className="text-center py-3 px-4">Estado</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const pct = r.total > 0 ? Math.round((r.completados / r.total) * 100) : 0;
                  const stateConfig = {
                    completada: { Icon: CheckCircle2, chip: 'chip-emerald', label: 'Completada' },
                    vencida:    { Icon: AlertTriangle, chip: 'chip-rose',    label: 'Vencida' },
                    pendiente:  { Icon: Clock,         chip: 'chip-amber',   label: 'Pendiente' },
                  }[r.derived];
                  return (
                    <tr key={r.id} className="border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors text-sm">
                      <td className="py-3 px-4">
                        <div className="text-white font-medium">{r.title}</div>
                        {r.description && (
                          <div className="text-[11px] text-white/50 line-clamp-1 max-w-md mt-0.5">{r.description}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-white/80">{r.curso_name}</td>
                      <td className="py-3 px-4 text-white/70">{r.docente_name}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col items-center gap-1 min-w-[110px]">
                          <span className="text-xs text-white/70 font-medium">
                            {r.completados} / {r.total}
                          </span>
                          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                pct === 100 ? 'bg-cubus-lime' : pct >= 50 ? 'bg-amber-400' : 'bg-rose-400'
                              }`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-white/70 text-xs">
                        {r.due_date ? fmtDateLong(r.due_date) : <span className="text-white/30">—</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`chip ${stateConfig.chip} inline-flex items-center gap-1`}>
                          <stateConfig.Icon size={11} />
                          {stateConfig.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatPill({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="glass-surface rounded-xl border border-white/[0.06] px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</p>
      <p className={`text-display text-2xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}
